import {
  CanvasField,
  HtmlCanvasParser,
  createHtmlParser,
} from '@contensis/html-canvas';
import EntriesMigrationService from './EntriesMigrationService';
import {
  CopyFieldResult,
  EntriesResult,
  MigrateRequest,
} from '~/models/Operations';
import { liquidEngine } from '~/providers/LiquidJS';
import ComposerToCanvasHtml from '~/transformations/ComposerToCanvasHtml';
import ContentTypeHelper from '~/util/content-type/ContentTypeHelper';
import { Logger } from '~/util/logger';
import { patchForDeterministicIds } from '~/util/polyfills';
import { isNullOrUndefined } from '~/util/typeguards';
import { isJson, tryParse } from '~/util/tryparse';

const validationError = (message: string) => {
  return new Error(message, { cause: 'validation' });
};

class CopyFieldService extends EntriesMigrationService {
  contentTypeHelper!: ContentTypeHelper;

  contentTypeId: string;
  fieldId: string;
  destinationId: string;
  template?: string;
  rootUri?: string;

  fields: {
    source?: ReturnType<ContentTypeHelper['resolve']['deepField']>;
    target?: ReturnType<ContentTypeHelper['resolve']['deepField']>;
  } = {};

  transformType:
    | 'none'
    | 'direct'
    | 'array'
    | 'flatten'
    | 'canvas'
    | 'integer' // value is parsed to integer
    | 'decimal' // value is parsed to float
    | 'boolean' // value is converted from boolean
    | 'truthy' // boolean calculated from truthy value
    | 'error' = 'none';

  private htmlCanvasParser: HtmlCanvasParser | undefined;
  private renderComposer: ComposerToCanvasHtml | undefined;
  private templateEngine = liquidEngine;

  constructor(payload: MigrateRequest, preview: boolean) {
    // Limit all entry searches to just this contentTypeId
    if (payload.copyField?.contentTypeId) {
      payload.query = {
        ...(payload.query || {}),
        contentTypeIds: [payload.copyField.contentTypeId],
        useDelivery: true,
        versionStatus: 'latest',
      };

      payload.transformGuids = false;
    }

    super(payload, preview);

    this.contentTypeId = payload.copyField?.contentTypeId || '';
    this.fieldId = payload.copyField?.fieldId || '';
    this.destinationId = payload.copyField?.destinationId || '';
    this.template = payload.copyField?.template;
    this.rootUri = payload.copyField?.rootUri;
  }

  /**
   * Migrate the contents of one field within a given content type to another field
   * in the same content type
   */
  MigrateFieldContent = async () => {
    await this.ResolveCopyFields();

    // get source entries so we can add in our new content
    await this.GetEntries({ withDependents: false });
    await this.TransformEntries();

    // commit or preview?
    const entriesResult = await this.MigrateEntries();

    // prepare result
    const result = this.MapCopyFieldResult(entriesResult);
    return result;
  };

  /**
   * Resolve a content type fieldId in source and destinationId in target repository
   * Checking field configurations for compatibility and determining the type of
   * transformation we will apply on this combination of source and destination field
   */
  ResolveCopyFields = async () => {
    const { contentTypeId, fieldId, destinationId, source } = this;

    // validate inputs
    if (!contentTypeId)
      throw validationError(`At least one contentTypeId is required`);
    if (!fieldId) throw validationError(`A fieldId to copy from is required`);
    if (!destinationId)
      throw validationError(`A destinationId to copy to is required`);

    // hydrate the source/target repos with content models
    const { contentTypes = [] } =
      (await this.HydrateContensisRepositories([contentTypeId])) || {};

    const contentTypeHelper = (this.contentTypeHelper = new ContentTypeHelper(
      contentTypes,
      source.context.components,
      source.context.tagGroups,
      source.context.project
    ));

    // resolve source field in sourceRepo
    this.fields.source = contentTypeHelper.resolve.deepField(
      fieldId,
      contentTypeId
    );
    const sourceField = this.fields.source?.field;

    // validate source field resolution
    if (!sourceField)
      throw validationError(
        `Did not find a field with id '${fieldId}' in content type ${contentTypeId} in ${source.projectId} on ${source.alias}`
      );

    // validate all target field resolutions
    for (const [_, target] of Object.entries(this.targets)) {
      // resolve target field in targetRepo
      this.fields.target = contentTypeHelper.resolve.deepField(
        destinationId,
        contentTypeId,
        target.context.contentTypes,
        target.context.components
      );
      const targetField = this.fields.target?.field;

      if (!targetField) {
        throw validationError(
          `Did not find a field with id '${destinationId}' in target content type ${contentTypeId} in ${target.projectId} on ${target.alias}`
        );
      } else {
        // check for specific conversions
        if (targetField.dataFormat === 'canvas') {
          // set up renderers for canvas transformation
          const client = await source.repo.deliveryApi;

          // patch node:crypto.randomUUID
          patchForDeterministicIds(
            `${source.alias}-${source.projectId}-${contentTypeId}-${sourceField.id}-${targetField.id}`
          );

          // set up a html-canvas parser
          const { parse } = await createHtmlParser({
            client,
            field: targetField as CanvasField,
            rootUri: this.rootUri,
          });
          this.htmlCanvasParser = parse;
          // initialise a composer renderer if we are transforming from a composer
          // that doesn't have a template using {{ source_value }}
          if (
            contentTypeHelper.field.assert.IsComposer(sourceField) &&
            !this.template?.includes('source_value')
          )
            this.renderComposer = new ComposerToCanvasHtml(
              contentTypeHelper,
              sourceField
            );

          // try throwing anything into canvas for now
          this.transformType = 'canvas';
        } else if (sourceField.dataType === 'boolean') {
          // convert a boolean field into a content field
          if (
            targetField.dataType.startsWith('string') ||
            targetField.dataType.startsWith('integer') ||
            targetField.dataType.startsWith('decimal')
          )
            this.transformType = 'boolean';
        } else if (targetField.dataType === 'boolean') {
          // truthy check on anything going into boolean fields
          this.transformType = 'truthy';
        } else if (targetField.dataType.startsWith('integer')) {
          // integer fields require any decimals truncated
          this.transformType = 'integer';
        } else if (targetField.dataType.startsWith('decimal')) {
          // parse anything going into decimals
          this.transformType = 'decimal';
        }

        // check for direct conversions where the field types match
        if (this.transformType === 'none')
          if (sourceField.dataType === targetField.dataType)
            this.transformType = 'direct';
          else if (
            targetField.dataType.startsWith(sourceField.dataType) &&
            targetField.dataType.endsWith('Array')
          )
            this.transformType = 'array';
          else if (
            sourceField.dataType.endsWith('Array') &&
            sourceField.dataType.startsWith(targetField.dataType)
          ) {
            this.transformType = 'flatten';
          }

        // ensure field types are compatible or skip this if a template is provided
        if (this.transformType === 'none' && !this.template) {
          throw validationError(
            `Can not transform ${contentTypeId}[${sourceField.id}]<${sourceField.dataType}> to ${contentTypeId}[${targetField.id}]<${targetField.dataType}> in ${target.projectId} on ${target.alias}`
          );
        }

        Logger.info(
          `OK to copy ${contentTypeId}[${sourceField.id}]<${
            sourceField.dataType
          }> field into ${contentTypeId}[${targetField.id}]<${
            targetField.dataType
          }> in ${target.projectId} on ${target.alias} [${this.transformType}${
            this.template ? '+template' : ''
          }]`,
          true
        );
      }
    }
  };

  /**
   * Add the new field value to the source entries,
   * applying transformations to the field value
   * and/or applying any provided template to modify the value
   * overwrites any existing data in destination field
   */
  TransformEntries = async () => {
    if (!this.fields.source?.field) return;

    const { fieldId, destinationId, source, template } = this;

    for (const entry of source.entries.raw) {
      let sourceEntryField = source.context.entry.getFieldValue(entry, fieldId);

      const existingDestinationValue = source.context.entry.getFieldValue(
        entry,
        destinationId
      );

      const templateVariables = {
        existing_value: existingDestinationValue,
        entry: entry,
        field: this.fields.source?.field,
        target_field: this.fields.target?.field,
      };
      // Allow templating to override the source entry field value
      if (template && template.includes('source_value'))
        sourceEntryField = await this.ApplyTemplate({
          source_value: sourceEntryField, // TODO: retire this in favour of using just {{value}}
          ...templateVariables,
        });

      // Transform the source entry field value into the target
      let destinationEntryField =
        await this.TransformEntryFieldValue(sourceEntryField);

      // Allow templating to override the target entry field value
      // if we're not overriding the source_value
      if (template && !template.includes('source_value')) {
        destinationEntryField = await this.ApplyTemplate({
          value: sourceEntryField,
          target_value: destinationEntryField,
          ...templateVariables,
        });
      }
      source.context.entry.updateFieldValue(
        entry,
        destinationId,
        destinationEntryField,
        this.fields.target?.parents
      );
    }
  };

  /**
   * Transforms a given entry's field value according to the determined `transformType`
   * @param fieldValue the value from the source field (the original field value)
   * @returns the transformed field value
   */
  //
  TransformEntryFieldValue = async (fieldValue: any) => {
    let destinationEntryField: any;

    switch (this.transformType) {
      case 'none':
      case 'direct':
        destinationEntryField = fieldValue;
        break;
      case 'array':
        // convert a flat field into an array field
        destinationEntryField = !isNullOrUndefined(fieldValue)
          ? [fieldValue]
          : fieldValue;
        break;
      case 'flatten': {
        // convert an array field into a flat field
        destinationEntryField = Array.isArray(fieldValue)
          ? fieldValue.join('\n') || null
          : fieldValue;
        break;
      }
      case 'boolean': {
        // convert a boolean field into a content field
        const { dataType } = this.fields.target?.field || {};
        if (dataType?.startsWith('string')) {
          destinationEntryField = fieldValue ? 'Yes' : 'No';
        } else if (
          dataType?.startsWith('integer') ||
          dataType?.startsWith('decimal')
        ) {
          destinationEntryField = fieldValue ? 1 : 0;
        } else {
          // should not be hit but just in case
          destinationEntryField = fieldValue;
        }
        break;
      }
      case 'truthy': {
        // convert any field into a boolean with a truthy check
        destinationEntryField = Array.isArray(fieldValue)
          ? !!fieldValue?.[0]
          : !!fieldValue;
        break;
      }
      case 'integer': {
        // parse any value to a clean integer
        destinationEntryField = Array.isArray(fieldValue)
          ? parseInt(fieldValue?.[0])
          : parseInt(fieldValue);
        break;
      }
      case 'decimal': {
        // parse any value to a float
        destinationEntryField = Array.isArray(fieldValue)
          ? parseFloat(fieldValue?.[0])
          : parseFloat(fieldValue);
        break;
      }
      case 'canvas': {
        // alter the source entry field with pre-rendered html if the source
        // is a composer and no template is used
        if (this.renderComposer) {
          const composerHtml = this.renderComposer.toHtml(fieldValue);
          destinationEntryField = await this.htmlCanvasParser?.(composerHtml);
        } else {
          // convert any array field into a flat field before parsing
          const parseValue = Array.isArray(fieldValue)
            ? fieldValue.join('\n')
            : fieldValue;
          destinationEntryField = !isNullOrUndefined(parseValue)
            ? await this.htmlCanvasParser?.(parseValue)
            : fieldValue;
        }
        break;
      }
    }

    return destinationEntryField;
  };

  /**
   * Apply a liquidjs tempate with a set of prepared variables
   */
  ApplyTemplate = async (templateVariables: { [key: string]: any }) => {
    const parsedTemplate = await this.templateEngine.parseAndRender(
      this.template || '{{ value }}',
      {
        ...templateVariables,
        __htmlToCanvasParser: this.htmlCanvasParser,
      }
    );
    const fieldValue = isJson(parsedTemplate)
      ? tryParse(parsedTemplate)
      : parsedTemplate;

    return fieldValue;
  };

  /**
   * Add additional info regarding the copy operation to the entries migration result
   * @param entriesResult The result of the entries migration
   * @returns The result of the copy field operation
   */
  MapCopyFieldResult = (entriesResult: EntriesResult) => {
    const { contentTypeId, source } = this;
    const result: CopyFieldResult = {
      copyField: {
        projectId: source.projectId,
        contentTypeId,
        field: this.fields.source?.field,
        targetField: Object.fromEntries(
          Object.keys(this.targetRepos).map((pId) => [
            pId,
            this.fields.target?.field,
          ])
        ),
        transformType: this.transformType,
      },
      ...entriesResult,
    };
    return result;
  };
}

export default CopyFieldService;
